import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { Footer } from "../../footer";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { themeFonts } from "../../../configs";
import { TermsOfServices } from "./terms-of-services";
import { CookiesList } from "./cookies-list";
import { CookiesPolicy } from "./cookies-policy";
import { AcceptablePolicy } from "./acceptable-policy";
import { PrivacyPolicyCcp } from "./privacy-policy-ccpa";
import PrivacySectionTableTwo from "./privacy-policy-table-2";
import PrivacySectionTable from "./privacy-section-table";
import { PrivacyPolicy } from "./privacy-policy";
import axios from "axios";
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
// Utility function to convert tab title to kebab-case query parameter value
const titleToQueryParam = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim();
};

const TermsConditions = ({
  showButton = false,
  acceptTerms,
}: {
  showButton?: any;
  acceptTerms?: any;
}) => {
  const Id = localStorage.getItem("userId");
  const token = localStorage.getItem("token");
  const isLoggedIn = token && Id;

  const isMediumScreen = useMediaQuery("(max-width:1350px)");
  const isSmallScreen = useMediaQuery("(max-width:980px)");

  const [value, setValue] = React.useState("Terms Of Service");

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };
  const [activeTab, setActiveTab] = useState("terms");

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const [policyData, setPolicyData] = useState<any>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTosApi = async (isRetry = false) => {
    try {
      if (!isRetry) {
        setLoading(true);
      }
      setError(null);

      const response = await axios.get(
        "https://api.pitchplaylists.com/api/tos",
        {
          timeout: 10000, // 10 second timeout
        }
      );

      // Validate response structure
      if (!response?.data) {
        throw new Error("Invalid response from server");
      }

      const tosData = response?.data?.TosTypeData;

      // Check if TosTypeData exists and is an array
      if (!tosData || !Array.isArray(tosData)) {
        throw new Error("No terms data available");
      }

      setPolicyData(tosData);
    } catch (error: any) {
      console.error("Error Fetching Data:", error);

      // Set appropriate error message based on error type
      let errorMessage = "Failed to load terms and conditions. Please try again.";

      if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        errorMessage = "Request timed out. Please check your connection and try again.";
      } else if (error.response?.status === 404) {
        errorMessage = "Terms and conditions not found.";
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setPolicyData([]); // Clear data on error
    } finally {
      setLoading(false);
    }
  };

  // Retry function
  const handleRetry = () => {
    fetchTosApi(true);
  };

  // Function to get the appropriate component based on title
  const getComponentByTitle = (title: string, data: any) => {
    switch (title) {
      case "Privacy Policy":
        return <PrivacyPolicy isMediumScreen={true} data={data} />;
      case "Privacy Policy for California Residents":
        return <PrivacyPolicyCcp isMediumScreen={true} data={data} />;
      case "Acceptable Use Policy":
        return <AcceptablePolicy isMediumScreen={true} data={data} />;
      case "Cookie policy":
        return <CookiesPolicy isMediumScreen={true} data={data} />;
      case "Terms Of Service":
        return <TermsOfServices isMediumScreen={true} data={data} />;
      case "Cookie List":
        return <CookiesList isMediumScreen={true} data={data} />;
      default:
        return <PrivacyPolicy isMediumScreen={true} data={data} />;
    }
  };
  const [showScrollIcon, setShowScrollIcon] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollIcon(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    fetchTosApi();
  }, []);

  // Handle query parameter navigation for tabs
  useEffect(() => {
    const contentParam = searchParams.get('content');
    if (contentParam && policyData?.length > 0) {
      // Find the tab that matches the query parameter
      const matchingIndex = policyData?.findIndex((item: any) => {
        const itemParam = titleToQueryParam(item?.title);
        return itemParam === contentParam;
      });

      if (matchingIndex !== -1) {
        setSelectedTabIndex(matchingIndex);
      }
    }
  }, [searchParams, policyData]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[500px] flex flex-col items-center justify-center gap-4">
          <CircularProgress
            size={40}
            sx={{
              color: "#ffffff",
              display: "inline-flex",
              alignItems: "center",
              fontWeight: 900,
            }}
          />
          <Typography className="text-white/60 text-[16px]">
            Loading terms and conditions...
          </Typography>
        </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-[500px] flex flex-col items-center justify-center gap-6 px-4">
          <div className="text-center max-w-md">
            <Typography className="text-[#FF6B6B] text-[20px] font-semibold mb-2">
              Unable to Load Content
            </Typography>
            <Typography className="text-white/60 text-[16px] mb-6">
              {error}
            </Typography>
            <button
              onClick={handleRetry}
              className="px-6 py-3 bg-[#58F2A9] text-black font-semibold rounded-full hover:opacity-80 transition-opacity"
            >
              Try Again
            </button>
          </div>
        </div>
    );
  }

  // Empty state
  if (!policyData || policyData.length === 0) {
    return (
      <div className="min-h-[500px] flex flex-col items-center justify-center gap-6 px-4">
          <Typography className="text-white/60 text-[20px] text-center max-w-md">
            No terms and conditions available at the moment.
          </Typography>
          <button
            onClick={handleRetry}
            className="px-6 py-3 bg-[#58F2A9] text-black font-semibold rounded-full hover:opacity-80 transition-opacity"
          >
            Retry
          </button>
        </div>
    );
  }

  return (
    <>
    <div
        className="content-body lg:py-[100px] py-[50px] pt-[120px] min-h-screen relative"
      >
        <section className="term-services-section">
          <div className="container">
            <div className="terms-content">
              <div className="terms-content-header md:mb-15 mb-8">
                <h1 className="2xl:text-[64px] lg:text-[50px] text-[32px] font-[700] text-[#fff] leading-[100%]">
                  Terms Of Service
                </h1>
              </div>
              <TabGroup
                selectedIndex={selectedTabIndex}
                onChange={(index) => {
                  setSelectedTabIndex(index);
                  // Update URL with query parameter
                  const selectedTitle = policyData[index]?.title;
                  if (selectedTitle) {
                    const paramValue = titleToQueryParam(selectedTitle);
                    navigate(`?content=${paramValue}`, { replace: true });
                  }
                }}
              >
                <TabList className="relative flex gap-[16px] flex-wrap list-none rounded-[999px] sm:mb-[66px] mb-4  ">
                  {policyData?.map((item: any, index: any) => (
                    <Tab as={Fragment} key={index}>
                      {({ selected }) => (
                        <button
                          onClick={() => handleTabChange("terms")}
                          className={`px-5 py-[14px] font-[600] rounded-[50px] transition-all ease-in-out border-0 cursor-pointer text-sm ${
                            selected
                              ? "bg-[#58F2A9] text-[#000] hover:bg-[#4AE5A0]"
                              : "bg-[#0E0E10] text-white hover:bg-[#1E2229]"
                          }`}
                        >
                          {item?.title}
                        </button>
                      )}
                    </Tab>
                  ))}
                </TabList>
                <TabPanels>
                  {policyData?.map((item: any, index: any) => (
                    <TabPanel key={item?._id || index}>
                      {getComponentByTitle(item?.title, item)}
                    </TabPanel>
                  ))}
                </TabPanels>
              </TabGroup>
            </div>
          </div>
          {/* Footer */}
          <div className="relative z-10 w-full mt-12 text-center mb-[60px] mt-[50px]">
            <div className="flex flex-col items-center gap-3 text-white text-[18px]">
              <button
                onClick={() => {
                  navigate('?content=terms-of-service', { replace: true });
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="transition-colors duration-200 block w-full hover:text-gray-300 cursor-pointer"
              >
                Terms of Service
              </button>
              <button
                onClick={() => {
                  navigate('?content=data-deletion-instructions-meta-facebook', { replace: true });
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="hover:text-white transition-colors duration-200 block w-full cursor-pointer"
              >
                Data Deletion Instructions (Meta/Facebook)
              </button>
            </div>
          </div>
        </section>
      </div>

      {showScrollIcon && (
        <div
          className="fixed bottom-6 right-6 z-50 w-12 h-12 hover:scale-110 transition-transform duration-300 cursor-pointer"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          title="Back to top"
        >
          <span className="border-2 border-white rounded-full w-10 h-10 inline-block ">
            {" "}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 100 100"
              fill="none"
              className="w-full h-full pb-[3px]"
            >
              <polyline
                points="35,60 50,45 65,60"
                stroke="white"
                strokeWidth="9"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      )}
    </>
  );
};

export default TermsConditions;
